import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Menu } from 'antd';
import { FilterOutlined } from '@ant-design/icons';

import TimecardHeaderButton from '../TimecardHeaderButton';
import Colors from '../../constants/Colors';
import {
  ACTIVE_USER_FILTER_ACTIVE,
  ACTIVE_USER_FILTER_ARCHIVED,
  ACTIVE_USER_FILTER_BOTH,
  activeUserFilterSetToMode,
  isDefaultActiveUserFilter,
} from './teamMembersActiveFilter.helpers';

const FILTER_MENU_ITEMS = [
  { key: ACTIVE_USER_FILTER_ACTIVE, label: 'Active users' },
  { key: ACTIVE_USER_FILTER_ARCHIVED, label: 'Archived users' },
  { key: ACTIVE_USER_FILTER_BOTH, label: 'Active & archived' },
];

export default function TeamMembersActiveFilterDropdown({
  activeUserFilterSet,
  onActiveUserFilterChange,
}) {
  const [visible, setVisible] = useState(false);
  const blockClose = useRef(false);
  const blockCloseTimer = useRef();

  const selectedMode = useMemo(
    () => activeUserFilterSetToMode(activeUserFilterSet),
    [activeUserFilterSet],
  );

  const filterIsDefault = useMemo(
    () => isDefaultActiveUserFilter(activeUserFilterSet),
    [activeUserFilterSet],
  );

  const onVisibleChange = (next) => {
    if (!next && blockClose.current) return;
    if (next) {
      blockClose.current = true;
      window.clearTimeout(blockCloseTimer.current);
      blockCloseTimer.current = window.setTimeout(() => {
        blockClose.current = false;
      }, 300);
    }
    setVisible(next);
  };

  const closeMenu = useCallback(() => setVisible(false), []);

  const onMenuClick = useCallback(({ key }) => {
    onActiveUserFilterChange(key);
    closeMenu();
  }, [closeMenu, onActiveUserFilterChange]);

  const overlay = useMemo(() => (
    <Menu
      className="team-members-active-filter-menu"
      selectedKeys={[selectedMode]}
      onClick={onMenuClick}
      style={{
        borderRadius: 6,
        border: `1px solid ${Colors.GRAY_200}`,
      }}
    >
      {FILTER_MENU_ITEMS.map(({ key, label }) => (
        <Menu.Item key={key}>{label}</Menu.Item>
      ))}
    </Menu>
  ), [onMenuClick, selectedMode]);

  return (
    <Dropdown
      visible={visible}
      onVisibleChange={onVisibleChange}
      trigger={['click']}
      placement="bottomRight"
      overlay={overlay}
      overlayClassName="team-members-active-filter-dropdown"
    >
      <span style={{ display: 'inline-flex' }}>
        <TimecardHeaderButton
          className={
            filterIsDefault
              ? 'timecards-header-filter-button'
              : 'timecards-header-filter-button is-active'
          }
          ariaLabel="Filter team members by status"
          onClick={(event) => event.preventDefault()}
          onMouseDown={(event) => event.preventDefault()}
        >
          <FilterOutlined style={{ marginLeft: 0 }} />
        </TimecardHeaderButton>
      </span>
    </Dropdown>
  );
}

TeamMembersActiveFilterDropdown.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  activeUserFilterSet: PropTypes.instanceOf(Set).isRequired,
  onActiveUserFilterChange: PropTypes.func.isRequired,
};
